import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ArchivePicker, ItemChips, imageUrl, itemTitle, useArchive, type ArchiveItem } from './archive';

export type StarterKind = 'original' | 'variant';

export const STARTERS: { kind: StarterKind; label: string }[] = [
  { kind: 'original', label: '기출문제 생성해보기' },
  { kind: 'variant', label: '기출문제의 변형 생성해보기' },
];

/** 1 문항 고르기 → 2 문항 입력(그림을 보고 직접) → 3 실행. 실행은 onRun 이 맡는다 */
export function StarterModal({ kind, onClose, onRun }: { kind: StarterKind; onClose: () => void; onRun: (text: string) => void }) {
  const { items, error } = useArchive();
  const [step, setStep] = useState(1);
  const [item, setItem] = useState<ArchiveItem>();
  const [text, setText] = useState('');
  const [reading, setReading] = useState('');
  const abort = useRef<AbortController | null>(null);
  const label = STARTERS.find((s) => s.kind === kind)!.label;

  // 모델 읽기는 하지 않는다(화면은 모델을 부르지 않는다). 그림을 보고 문항을 직접 붙여 넣는다
  const read = useCallback(async (target: ArchiveItem) => {
    abort.current?.abort();
    setText('');
    setReading(`${itemTitle(target)} 의 그림을 보고 문항 원문을 아래에 붙여 넣으십시오`);
  }, []);

  useEffect(() => () => abort.current?.abort(), []);
  useEffect(() => { if (step === 2 && item) void read(item); }, [step, item, read]);

  return (
    <div className="modal" role="dialog" aria-modal="true">
      <div className="panel modal__box">
        <div className="panel__head">
          <span>{label}</span>
          <span className="tabs">{['문항 고르기', '문항 입력', '실행'].map((s, i) => <button key={s} type="button" className={step === i + 1 ? 'is-on' : undefined} disabled>{i + 1} {s}</button>)}</span>
        </div>
        <div className="panel__body modal__body">
          {step === 1 ? (
            <>
              {error ? <div className="notice">기출 목록을 불러오지 못함: {error}</div> : null}
              <ArchivePicker items={items} onPick={setItem} />
              {item ? (
                <div className="modal__preview">
                  <div className="archive__row"><span className="chip">{itemTitle(item)}</span> <ItemChips item={item} /></div>
                  <img className="archive__image" src={imageUrl(item)} alt={item.id} />
                </div>
              ) : null}
            </>
          ) : (
            <>
              <div className="notice">{reading}</div>
              {item ? <img className="archive__image" src={imageUrl(item)} alt={item.id} /> : null}
              <textarea className="modal__text" value={text} rows={8} onChange={(e) => setText(e.target.value)} />
            </>
          )}
        </div>
        <div className="modal__foot">
          <span className="seg"><button type="button" onClick={onClose}>닫기</button></span>
          <span className="seg">
            {step === 2 ? <button type="button" onClick={() => setStep(1)}>이전</button> : null}
            {step === 2 && item ? <button type="button" onClick={() => void read(item)}>다시 읽기</button> : null}
            {step === 1 ? <button type="button" className="is-on" disabled={!item} onClick={() => setStep(2)}>다음</button> : null}
            {step === 2 ? <button type="button" className="is-on" disabled={!text.trim()} onClick={() => { setStep(3); onRun(text.trim()); }}>{kind === 'variant' ? '변형 만들기' : '그리기'}</button> : null}
          </span>
        </div>
      </div>
    </div>
  );
}
